import React, { useState } from 'react';
import WeekCard from './WeekCard';
import { applyFlkFilter } from './plannerUtils';
import { api } from '../../shared/utils/api';

// ─── DayColumn — week view ────────────────────────────────────────────────────

// Adaptive max cards per column — taller tiers show fewer before collapsing
function getColMax(detailLevel) {
  if (detailLevel === 'full')  return 3;
  if (detailLevel === 'clean') return 5;
  return 4; // standard
}

function DayColumn({ dateStr, dayData, allWeekDates, onComplete, onMove, onAddEntry, isToday, isPast, flkFilter, detailLevel, colourMode, hideCompleted, onSwitchToDayView, expanded, onExpand, onSkipDay }) {
  const [skipConfirm, setSkipConfirm] = useState(false);
  const [skipping, setSkipping] = useState(false);

  const rawEntries = dayData.entries || [];
  let entries = applyFlkFilter(rawEntries, flkFilter);
  if (hideCompleted) entries = entries.filter(e => e.status !== 'completed');
  const { is_study_day } = dayData;
  const completed_count = entries.filter(e => e.status === 'completed').length;
  const total_count = entries.length;
  const pending_count = entries.filter(e => e.status === 'pending').length;

  const d = new Date(dateStr + 'T00:00:00');
  const dayLabel = d.toLocaleDateString('en-GB', { weekday: 'short' });
  const dateLabel = d.getDate();
  const colMax = getColMax(detailLevel);
  const visibleEntries = expanded ? entries : entries.slice(0, colMax);
  const hiddenCount = expanded ? 0 : entries.length - colMax;

  const handleSkip = async () => {
    setSkipping(true);
    const res = await api('/api/planner/skip-day', {
      method: 'POST',
      body: JSON.stringify({ date: dateStr }),
    });
    setSkipping(false);
    setSkipConfirm(false);
    if (res && !res.error && onSkipDay) onSkipDay(dateStr, res);
  };

  return (
    <div className={`week-col${isToday ? ' week-col-today' : ''}`}>
      <div className="week-col-head">
        {dayLabel} {dateLabel}
        {is_study_day && pending_count > 0 && !skipConfirm && (
          <button className="week-skip-link" onClick={() => setSkipConfirm(true)}>Skip</button>
        )}
        {skipConfirm && (
          <div className="week-skip-confirm">
            <span>{pending_count} topic{pending_count !== 1 ? 's' : ''} will be redistributed.</span>
            <div className="week-skip-confirm-actions">
              <button onClick={handleSkip} disabled={skipping}>{skipping ? '…' : 'Skip'}</button>
              <button onClick={() => setSkipConfirm(false)} disabled={skipping}>No</button>
            </div>
          </div>
        )}
      </div>
      {is_study_day && total_count > 0 && (
        <div className="week-col-count">{completed_count} of {total_count}</div>
      )}
      {!is_study_day ? (
        <div style={{ fontSize: '11px', color: 'var(--text-tertiary)', padding: '4px 0' }}>Rest day</div>
      ) : (
        <>
          <div className="week-cards">
            {visibleEntries.map(entry => (
              <WeekCard
                key={entry.id}
                entry={entry}
                dateStr={dateStr}
                detailLevel={detailLevel}
                colourMode={colourMode}
                onSwitchToDayView={onSwitchToDayView}
              />
            ))}
          </div>
          {hiddenCount > 0 && (
            <div className="week-more" onClick={onExpand}>
              +{hiddenCount} more
            </div>
          )}
        </>
      )}
    </div>
  );
}

// ─── WeekView ────────────────────────────────────────────────────────────────

export default function WeekView({ weekData, weekDates, today, flkFilter, detailLevel, colourMode, hideCompleted, sortSubject, onComplete, onMove, onAddEntry, onSwitchToDayView, weekGridRef, weekScrollLeft, weekScrollRight, onScrollWeekGrid, onSkipDay }) {
  const [expanded, setExpanded] = useState(false);

  return (
    <>
      {weekData?.is_projected && (
        <div className="week-projected">Projected — will adjust as you add data</div>
      )}
      {expanded && (
        <div style={{ textAlign: 'right', marginBottom: 6 }}>
          <button className="week-collapse-btn" onClick={() => setExpanded(false)}>Show less ↑</button>
        </div>
      )}
      <div className="week-grid-wrapper">
        {weekScrollLeft && (
          <button className="week-scroll-btn week-scroll-btn--left" onClick={() => onScrollWeekGrid(-1)}>‹</button>
        )}
        {weekScrollRight && (
          <button className="week-scroll-btn week-scroll-btn--right" onClick={() => onScrollWeekGrid(1)}>›</button>
        )}
        <div className="week-grid" ref={weekGridRef} style={{ overflowX: 'auto' }}>
          {weekDates.map(dateStr => {
            const dayData = weekData.days[dateStr];
            if (!dayData) return null;
            return (
              <DayColumn
                key={dateStr}
                dateStr={dateStr}
                dayData={dayData}
                allWeekDates={weekDates}
                onComplete={onComplete}
                onMove={onMove}
                onAddEntry={onAddEntry}
                isToday={dateStr === today}
                isPast={dateStr < today}
                flkFilter={flkFilter}
                detailLevel={detailLevel}
                colourMode={colourMode}
                hideCompleted={hideCompleted}
                onSwitchToDayView={onSwitchToDayView}
                expanded={expanded}
                onExpand={() => setExpanded(true)}
                onSkipDay={onSkipDay}
              />
            );
          })}
        </div>
      </div>
    </>
  );
}
